import { Link } from 'react-router-dom';
import { MapPin, Download, Info, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAIL_LABELS = {
  housing: 'Housing', meter_rent: 'Meter for Rent', truck_rent: 'Truck for Rent',
  first_payment: 'First Payment', task: 'Task', spots_available: 'Spots Available',
  underground: 'Underground', aerial: 'Aerial', mdu: 'MDU',
  walfish: 'Walfish', prewired: 'Prewired', project_starting: 'Project Starting'
};

const KEY_FACTS = ['spots_available', 'project_starting'];

const MarketLogo = ({ market, className }) => (
  market.has_logo ? (
    <div className={`bg-white flex items-center justify-center ${className}`}>
      <img src={`${API_URL}/markets/${market.id}/logo`} alt={market.title} className="max-h-full max-w-full object-contain" />
    </div>
  ) : (
    <div className={`bg-[#050507] flex items-center justify-center ${className}`}>
      <MapPin className="text-slate-700" size={32} />
    </div>
  )
);

export const MarketCard = ({ market, onMore }) => {
  const d = market.details || {};
  const facts = KEY_FACTS.filter((k) => d[k]);

  return (
    <div
      className="group w-full max-w-xs sm:w-72 sm:max-w-none bg-[#12121A] rounded-xl border border-slate-800 hover:border-violet-500/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-300 overflow-hidden flex flex-col"
      data-testid={`landing-market-${market.id}`}
    >
      <MarketLogo market={market} className="h-28 sm:h-32 p-4" />
      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <h3 className="text-base sm:text-lg font-bold text-white mb-2 line-clamp-2" style={{ fontFamily: 'Syne, sans-serif' }}>
          {market.title}
        </h3>
        {facts.length > 0 && (
          <div className="space-y-1 mb-4">
            {facts.map((k) => (
              <p key={k} className="text-sm truncate" data-testid={`market-fact-${k}-${market.id}`}>
                <span className="text-slate-500">{DETAIL_LABELS[k]}: </span>
                <span className="text-slate-200 font-medium">{d[k]}</span>
              </p>
            ))}
          </div>
        )}
        <div className="mt-auto flex gap-2">
          <button
            onClick={() => onMore(market)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold text-slate-200 border border-slate-700 hover:border-violet-500/60 hover:text-white hover:bg-violet-500/10 transition-colors"
            data-testid={`market-info-${market.id}`}
          >
            <Info size={16} />
            Info
          </button>
          <Link
            to="/register"
            className="flex-1 inline-flex items-center justify-center py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 transition-colors"
            data-testid={`apply-market-${market.id}`}
          >
            Apply Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export const MarketDetailsDialog = ({ market, onClose }) => {
  const details = Object.entries(market?.details || {}).filter(([, v]) => v);

  return (
    <Dialog open={!!market} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="own-type-scale bg-[#12121A] border-slate-800 text-white w-[calc(100%-2rem)] max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto p-5 sm:p-7"
        data-testid="market-details-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {market && (
          <>
            <div className="flex items-center gap-4 pr-6">
              <MarketLogo market={market} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg p-2 flex-shrink-0" />
              <div className="min-w-0">
                <DialogTitle className="text-lg sm:text-2xl font-bold text-white leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {market.title}
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-xs sm:text-sm mt-1">
                  Market details & requirements
                </DialogDescription>
              </div>
            </div>

            {market.description && (
              <p className="text-slate-300 text-sm sm:text-base whitespace-pre-line" data-testid="market-dialog-description">
                {market.description}
              </p>
            )}

            {details.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="market-dialog-details">
                {details.map(([key, val]) => (
                  <div key={key} className="bg-[#0A0A0F] border border-slate-800 rounded-lg px-3 py-2.5">
                    <span className="text-slate-500 text-xs uppercase tracking-wide">{DETAIL_LABELS[key] || key}</span>
                    <p className="text-slate-100 text-sm font-medium break-words whitespace-pre-line">{val}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-800">
              {market.has_pdf && (
                <a
                  href={`${API_URL}/markets/${market.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-violet-300 border border-violet-500/40 hover:bg-violet-500/10 transition-colors mt-3"
                  data-testid={`download-pdf-${market.id}`}
                >
                  <Download size={16} />
                  {market.pdf_title || 'Download PDF'}
                </a>
              )}
              <Link
                to="/register"
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 transition-colors mt-3"
                data-testid={`dialog-apply-market-${market.id}`}
              >
                Apply Now
                <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
